<template>
  <div>
    <div class="text-center">
      <div id="mainnav">
        <OnClickOutside @trigger="close">
          <!-- Mobile toggle button -->
          <div 
            class="lg:hidden p-2 cursor-pointer relative z-[60]"
            @click="openmobilenav = !openmobilenav"
          >
            <i class="fa text-xl text-gray-700" :class="openmobilenav ? 'fa-times' : 'fa-bars'"></i>
          </div>
          
          <!-- Navigation menu -->
          <ul 
            class="hidden lg:inline-flex list-none m-0 p-0 text-left"
            :class="{ 
              'fixed! inset-0! flex! flex-col! bg-white! z-50! pt-16! overflow-y-auto! lg:relative! lg:flex-row! lg:pt-0! lg:bg-transparent! lg:overflow-visible!': openmobilenav 
            }"
          >
            <li 
              :id="`li-${menuItem.title}`" 
              v-for="(menuItem, index) in menulist" 
              :key="index"
              class="relative group"
              :class="menuItem.megamenu ? 'static' : ''"
              @mouseenter="setHover(menuItem.title)"
              @mouseleave="clearHover"
            >
              <template v-if="menuItem.authOnly === false || (menuItem.authOnly === true && isAuthenticated)">
                <!-- Menu item without path (dropdown trigger) -->
                <div 
                  v-if="!menuItem.path" 
                  class="flex items-center px-4 py-2 lg:h-[70px] text-gray-600 text-sm font-semibold uppercase tracking-wider cursor-pointer hover:text-[#009d66] hover:bg-gray-50 transition-colors duration-200 relative"
                  @click="setActive(menuItem.title)"
                >
                  {{ $t(`menu.${menuItem.key}`, menuItem.title) }}
                  <span 
                    v-if="menuItem.children || menuItem.megamenu"
                    class="ml-1 text-[10px]"
                  >▼</span>
                </div>

                <!-- Menu item with path (direct link) -->
                <NuxtLinkLocale 
                  v-else 
                  :to="menuItem.path" 
                  class="flex items-center px-4 py-2 lg:h-[70px] text-gray-600 text-sm font-semibold uppercase tracking-wider hover:text-[#009d66] hover:bg-gray-50 transition-colors duration-200 relative"
                  @click="setActive(menuItem.title)"
                >
                  {{ $t(`menu.${menuItem.key}`, menuItem.title) }}
                  <span 
                    v-if="menuItem.children || menuItem.megamenu"
                    class="ml-1 text-[10px]"
                  >▼</span>
                </NuxtLinkLocale>

                <!-- Submenu dropdown -->
                <ul 
                  :ref="(el) => menuseStore.setMenuRefs(menuItem.title, el)" 
                  class="absolute left-0 z-40 bg-white shadow-md py-4 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 lg:top-full"
                  :class="{ 'opacity-100! visible!': isActive(menuItem.title) }" 
                  v-if="menuItem.children"
                >
                  <li 
                    class="border-b border-gray-100 last:border-0 relative group/child" 
                    :id="`li-nav-${childrenItem.key}`"
                    v-for="(childrenItem, index) in menuItem.children" 
                    :key="index"
                  >
                    <!-- Child item with sub-children -->
                    <a 
                      href="#" 
                      @click.prevent="setActiveChild(childrenItem.title)" 
                      v-if="childrenItem.children"
                      class="block px-6 py-2 text-sm text-gray-700 hover:text-white hover:bg-[#009d66] transition-colors relative"
                    >
                      {{ $t(`menu.${childrenItem.key}`, childrenItem.title) }}
                      <span v-if="childrenItem.children" class="absolute right-4 top-1/2 -translate-y-1/2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24"
                          stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </a>
                    
                    <!-- Child item direct link -->
                    <template v-else>
                      <NuxtLinkLocale
                        @click="setActiveAndUpdateState({ menuChildItem: childrenItem.title, query: childrenItem.query, path: childrenItem.path })"
                        href="#"
                        class="block px-6 py-2 text-sm text-gray-700 hover:text-white hover:bg-[#009d66] transition-colors"
                      >
                        {{ $t(`menu.${childrenItem.key}`, childrenItem.title) }}
                      </NuxtLinkLocale>
                    </template>

                    <!-- Third level submenu -->
                    <ul 
                      :id="`child-ul-${childrenItem.title}-${index}`"
                      :ref="(el) => menuseStore.setMenuRefs(`child-ul-${menuItem.title}-${index}`, el)"
                      class="absolute left-full top-0 z-50 bg-white shadow-md py-4 min-w-[220px] opacity-0 invisible transition-all duration-200 group-hover/child:opacity-100 group-hover/child:visible"
                      :class="{ 'opacity-100! visible!': isActiveChild(childrenItem.title) }"
                      v-if="childrenItem.children"
                    >
                      <li 
                        class="border-b border-gray-100 last:border-0" 
                        :id="childrenSubItem.key"
                        v-for="(childrenSubItem, index) in childrenItem.children" 
                        :key="index"
                      >
                        <NuxtLinkLocale 
                          :to="{ path: childrenSubItem.path }"
                          class="block px-6 py-2 text-sm text-gray-700 hover:text-white hover:bg-[#009d66] transition-colors"
                        >
                          {{ $t(`menu.${childrenSubItem.key}`, childrenSubItem.title) }}
                        </NuxtLinkLocale>
                      </li>
                    </ul>
                  </li>
                </ul>
                
                <!-- Mega menu container -->
                <div 
                  class="absolute left-0 z-40 bg-white shadow-md p-8 w-full opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                  :class="{ 'opacity-100! visible!': isActive('portfolio') }"
                  v-if="menuItem.megamenu"
                >
                  <div class="container mx-auto">
                    <div class="flex flex-wrap -mx-4">
                      <div 
                        class="w-1/5 px-4" 
                        v-for="(childrenItem, index) in menuItem.children" 
                        :key="index"
                      >
                        <div>
                          <div class="mb-3 cursor-pointer" @click="setActivesubmega('portfolio')">
                            <h5 class="font-bold text-sm capitalize">
                              {{ childrenItem.title }}
                              <span class="inline-block ml-1 after:content-['▼'] after:text-[10px] lg:hidden"></span>
                            </h5>
                          </div>
                          <div 
                            class="block lg:block!"
                            :class="{ 'block!': isActivesubmega('portfolio') }"
                          >
                            <ul class="space-y-1">
                              <li 
                                v-for="(childrenSubItem, index) in childrenItem.children" 
                                :key="index"
                              >
                                <NuxtLinkLocale 
                                  :to="{ path: childrenSubItem.path }"
                                  class="text-sm text-gray-700 hover:text-primary transition-colors"
                                >
                                  {{ childrenSubItem.title }}
                                </NuxtLinkLocale>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </li>
          </ul>
        </OnClickOutside>
      </div>
    </div>
  </div>
</template>
<script setup>
import { useMenuStore } from "~/store/menu";
import { OnClickOutside } from "@vueuse/components/index";
import { useUserStore } from "~/store/UserStore";
import { useFilterStore } from "~/store/FilterStore";
import { useCategoryStore } from "~/store/CategoryStore";
import promoQueriesJson from "~/data/promo.query.json";
import { useUtilityStore } from "~/store/UtililityStore";
const userStore = useUserStore();
const filterStore = useFilterStore();
const categoryStore = useCategoryStore();
const utilityStore = useUtilityStore();
const isAuthenticated = computed(() => userStore.isAuthenticated)
const selectedLanguage = computed(() => utilityStore.selectedLanguage);
const localePath = useLocalePath()
const parentCategoriesWithPromo = computed(() => categoryStore.parentCategoriesWithPromo);

const router = useRouter();
const promoQueries = ref(promoQueriesJson)

const promoChilds = computed(() => {
  if (!parentCategoriesWithPromo.value || !Array.isArray(parentCategoriesWithPromo.value) || parentCategoriesWithPromo.value.length === 0) {
    return [];
  }
  
  const filtered = parentCategoriesWithPromo.value
    .filter(x => {
      if (!x || !x.key) {
        return false;
      }
      
      const docCount = x.doc_count;
      
      // Only include categories with promo products (doc_count > 0)
      if (!docCount || docCount <= 0) {
        return false;
      }
      
      return true;
    })
    .map(m => {
      // Try to get the predefined promo query for this category
      let promoData = promoQueries.value[m.key];
      
      // Extract category title from API response
      let categoryTitle = 'Promo';
      if (m.parent_top_hits?.hits?.hits?.[0]?._source?.Values?.category_tree?.[0]?.Data?.[0]) {
        const categoryData = m.parent_top_hits.hits.hits[0]._source.Values.category_tree[0].Data[0];
        const labels = categoryData.Labels;
        categoryTitle = labels[selectedLanguage.value] || labels['en_GB'] || Object.values(labels)[0] || m.key;
      }
      
      // If not found, create a default promo query for this category
      if (!promoData) {
        console.log(`⚠️ No predefined query for ${m.key}, creating default promo query`);
        promoData = {
          attribute: {
            price_promo: {
              "price promo": true
            }
          },
          categories: [m.key],
          labels: {
            [selectedLanguage.value]: categoryTitle,
            'en_GB': categoryTitle
          }
        };
      } else {
        // Use label from predefined query
        const labels = promoData.labels;
        categoryTitle = labels[selectedLanguage.value] || labels['en_GB'] || Object.values(labels)[0] || categoryTitle;
      }
      
      return {
        "path": "/products/search",
        "key": m.key,
        "title": categoryTitle + " +" + m.doc_count,
        "type": "link",
        "query": promoData
      }
    });
  
  return filtered;
})

const calenderData = ref([
  {
    key: 'event1',
    label: 'Event One',
    image: 'https://example.com/event1.jpg',
  },
  {
    key: 'event2',
    label: 'Event Two',
    image: 'https://example.com/event2.jpg',
  },
  {
    key: 'event3',
    label: 'Event Three',
    image: 'https://example.com/event3.jpg',
  }
]);
const calenderChilds = computed(() => calenderData.value?.map(c => {
  return {
    "path": "/calender",
    "key": c.key,
    "title": c.label,
    "type": "link",
    "image": c.image
  }
}))


const props = defineProps(["leftSidebarVal"]);
const openmobilenav = ref(false);
const subnav = ref(false);
const activeItem = ref("home");
const activeChildItem = ref("");
const activemegaChild = ref("portfolio");
const menuseStore = useMenuStore();

const cldUser = computed(() => userStore.cldUser);
const isApiGatewayAccess = computed(() => {
  return cldUser.value?.roles?.includes('API-Gateway-Access');
});

const menulist = computed(() => {
  const menus = menuseStore.data;
  const menusWithoutAccount = menus.filter((menu) => menu.key !== "account");
  
  // Filter out dropship_order_history
  if (!isApiGatewayAccess.value) {
    return menusWithoutAccount.map(menu => {
      if (menu.children && Array.isArray(menu.children)) {
        return {
          ...menu,
          children: menu.children.map(child => {
            if (child.children && Array.isArray(child.children)) {
              return {
                ...child,
                children: child.children.filter(item => item.key !== 'dropship_order_history')
              };
            }
            return child;
          })
        };
      }
      return menu;
    });
  }
  
  return menusWithoutAccount;
});

const isActive = (menuItem) => {
  return activeItem.value === menuItem;
};
const setActive = (menuItem) => {
  if (activeItem.value === menuItem) {
    activeItem.value = "";
  } else {
    activeItem.value = menuItem;
  }
};
const setHover = (menuItem) => {
  activeItem.value = menuItem;
};
const clearHover = () => {
  activeItem.value = "";
};
const isActiveChild = (menuChildItem) => {
  return activeChildItem.value === menuChildItem;
};
const setActiveChild = (menuChildItem) => {
  if (activeChildItem.value === menuChildItem) {
    activeChildItem.value = "";
  } else {
    activeChildItem.value = menuChildItem;
  }
};
const setActiveAndUpdateState = ({ menuChildItem, query, path, }) => {
  if (activeChildItem.value === menuChildItem) {
    activeChildItem.value = "";
  } else {
    activeChildItem.value = menuChildItem;
  }
  
  if (!query) {
    router.push(localePath(path))
    return;
  }
  
  const {
    attribute,
    categories,
    releaseWeek, 
    releaseYear,
    expandedCategories,
    categorySelected,
    sort,
  } = query;
  
  if (attribute) {
    filterStore.setSelectedAttributeFilters(typeof attribute === "string" ? JSON.parse(attribute) : attribute)
    filterStore.setSelectedReleaseFilters({})
    filterStore.setSelectedCategoryFilters([])
  }
  
  if (categories && categories.length) {
    filterStore.setSelectedCategoryFilters(categories)
    filterStore.setSelectedReleaseFilters({})
  }
  
  if (releaseWeek && releaseYear) {
    filterStore.setSelectedReleaseFilters({
      releaseWeek,
      releaseYear,
    })
    filterStore.setSelectedAttributeFilters({})
    filterStore.setSelectedCategoryFilters([])
  }

  if (expandedCategories) {
    filterStore.setExpandedCategoryFilters(expandedCategories)
  }
  
  if (categorySelected) {
    filterStore.setCategoryfilterState(categorySelected)
  }
  
  if (sort) {
    filterStore.setSelectedSort(sort)
  }

  // Build URL parameters from filter object
  const filterParams = new URLSearchParams();

  // Add attribute filters
  if (attribute && Object.keys(attribute).length) {
    for (const [attrKey, attrValues] of Object.entries(attribute)) {
      for (const [valueKey, isSelected] of Object.entries(attrValues)) {
        if (isSelected === true) {
          filterParams.append(`filter[${attrKey}]`, valueKey);
        }
      }
    }
  }

  // Add sort if present
  if (sort) {
    filterParams.append("sort", sort);
  }

  // Navigate to search page with filter parameters
  const url = `${localePath(path)}${filterParams.toString() ? '?' + filterParams.toString() : ''}`;
  router.push(url);
};


const redirect = (childrenItem) => {
  if (activeChildItem.value === childrenItem.title) {
    activeChildItem.value = "";
  } else {
    activeChildItem.value = childrenItem.title;
  }

  const queryprams = {};

  if (childrenItem.query) {
    queryprams["data"] = JSON.stringify(childrenItem.query);
    router.push({
      path: localePath(childrenItem.path),
      query: queryprams,
    });
  } else {
    router.go({
      path: childrenItem.path,
    });
  }
};
const isActivesubmega = (megaChildItem) => {
  return activemegaChild.value === megaChildItem;
};
const setActivesubmega = (megaChildItem) => {
  if (activemegaChild.value === megaChildItem) {
    activemegaChild.value = "";
  } else {
    activemegaChild.value = megaChildItem;
  }
};



onBeforeMount(() => {
  menuseStore.addChildToMenu('release')
})

onMounted(async () => {
  menuseStore.addChildToCalender("calender", calenderChilds.value)
  
  // Set promo menu when data is available
  menuseStore.addChildToPromo("promo", promoChilds.value || [])
  
  // Also update after a brief delay to ensure data has loaded
  await new Promise(resolve => setTimeout(resolve, 600));
  menuseStore.addChildToPromo("promo", promoChilds.value || [])
})

// Watch for changes in promo categories or language
watch(() => parentCategoriesWithPromo.value, (newVal) => {
  menuseStore.addChildToPromo("promo", promoChilds.value || [])
}, { deep: true })

// Watch for language changes
watch(() => selectedLanguage.value, () => {
  menuseStore.addChildToPromo("promo", promoChilds.value || [])
})


const close = () => {
  openmobilenav.value = false;
};


</script>

<style scoped>
/* Mobile menu toggle positioning */
/* No @apply needed - using inline Tailwind classes */
</style>
